<template>
    <div>
        <div class="button-container">
            <button class="btn btn-primary" @click.prevent="generatePDF">Descargar PDF</button>
        </div>

        <div v-if="sale" id="invoice" class="invoice-container">
            <!-- Encabezado -->
            <header>
                <div class="logo">
                    <h1>iDAMedic</h1>
                </div>
                <div class="invoice-title">
                    <h2>Orden de venta</h2>
                </div>
            </header>

            <hr />

            <!-- Fecha e Invoice ID -->
            <div class="invoice-info">
                <p><strong>Fecha:</strong> {{ sale.sale_date_format }}</p>
                <p v-if="sale.sales_status"><strong>{{ sale.sales_status.name }}
                    </strong> | <strong>Venta No:</strong> {{ sale.id }}</p>
            </div>

            <!-- Información del cliente y del vendedor -->
            <section class="customer-payment-info">
                <div>
                    <p><strong>Paciente:</strong></p>
                    <p v-if="sale.patient">{{ sale.patient.last_name }} {{ sale.patient.first_name }}</p>
                    <p v-else>No patient information available</p>
                    <p v-if="sale.patient">{{ sale.patient.email }}</p>
                    <p v-else>No patient information available</p>
                </div>
                <div>
                    <p><strong>Médico:</strong></p>
                    <p v-if="sale.doctor">{{ sale.doctor.name }}</p>
                    <p v-else>No doctor information available</p>
                    <p v-if="sale.doctor">{{ sale.doctor.email }}</p>
                    <p v-else>No doctor information available</p>
                </div>
            </section>

            <!-- Tabla de servicios -->
            <table class="styled-table">
                <thead>
                    <tr class="center-text">
                        <th>Nombre</th>
                        <th>Cant.</th>
                        <th>Precio.</th>
                        <th>Desc.</th>
                        <th>Subtotal</th>
                        <th>IVA</th>
                        <th>Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
v-for="detail in sale.sale_details" :key="detail.id"
                        :class="{ 'refund-row': detail.is_refund === true }">
                        <td>{{ detail.item.name }} </td>
                        <td>{{ detail.quantity }}</td>
                        <td>{{ detail.item.price }}</td>
                        <td>${{ detail.discount }}</td>
                        <td>${{ detail.subtotal }}</td>
                        <td>${{ detail.tax }}</td>
                        <td>${{ detail.total }}</td>
                    </tr>
                </tbody>
            </table>

            <!-- Totales -->
            <div class="totals-container">
                <table class="totals-table">
                    <tr>
                        <td>Monto Total:</td>
                        <td>${{ sale.total_amount }}</td>
                    </tr>
                    <tr>
                        <td>Descuento Total:</td>
                        <td>${{ sale.discount }}</td>
                    </tr>
                    <tr>
                        <td>Subtotal (con descuentos):</td>
                        <td>${{ sale.subtotal }}</td>
                    </tr>
                    <tr>
                        <td>IVA:</td>
                        <td>${{ sale.tax }}</td>
                    </tr>
                    <tr class="total">
                        <td><strong>Total a Pagar:</strong></td>
                        <td class="amount-column"><strong>${{ sale.final_amount }}</strong></td>
                    </tr>
                </table>
            </div>

            <!-- Nota -->
            <p class="note"><strong>NOTA:</strong> Este es un recibo generado por computadora y no requiere firma
                física.</p>
        </div>
        <div v-else>
            <p>Sin Información</p>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, onUnmounted } from 'vue';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
const { proxy } = getCurrentInstance();

const sale = ref(null);

const updateSaleHandler = (updateSale) => {
    sale.value = updateSale;
};

onMounted(() => {
    proxy.$eventBus.on('saleUpdated', updateSaleHandler);
});

onUnmounted(() => {
    proxy.$eventBus.off('saleUpdated', updateSaleHandler);
});

const generatePDF = () => {
    if (!sale.value) {
        alert('Sin permisos para generar PDF');
        return;
    }

    const invoiceElement = document.getElementById('invoice');

    html2canvas(invoiceElement, { scale: 2, logging: false }).then((canvas) => {
        const imgData = canvas.toDataURL('image/jpeg');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, 'JPEG', 0, 10, imgWidth, imgHeight);
        pdf.save(`Orden${sale.value.id}.pdf`);
    });
};
</script>

<style scoped>
/* Contenedor principal */
.invoice-container {
    max-width: 900px;
    margin: auto;
    padding: 20px;
    border: 1px solid #ddd;
    background-color: #fff;
    font-family: "Arial", sans-serif;
}

.button-container {
    text-align: center;
}

/* Encabezado */
header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.logo h1 {
    font-size: 2rem;
    color: #3466af;
    font-weight: bold;
}

.invoice-title h2 {
    font-size: 1.8rem;
}

/* Línea divisoria */
hr {
    border: 0;
    height: 1px;
    background: #ddd;
    margin: 10px 0;
}

/* Información de la factura */
.invoice-info {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
}

/* Sección de cliente y empresa */
.customer-payment-info {
    display: flex;
    justify-content: space-between;
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 5px;
    margin-bottom: 20px;
}

.customer-payment-info p {
    margin: 2px 0;
    line-height: 1.2;
}

.customer-payment-info div {
    width: 45%;
}

/* Tabla de servicios */
.styled-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 20px;
}

.styled-table th,
.styled-table td {
    border: 1px solid #ddd;
    padding: 10px;
    text-align: center;
    /* Asegura que todo el contenido de la tabla esté centrado */
    vertical-align: middle;
    /* Asegura alineación vertical correcta */
}

.styled-table th {
    background-color: #f4f4f4;
    font-weight: bold;
    text-align: center;
}

/* Tabla de totales */
.totals-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 20px;
    border: 1px solid #ddd;
}

.totals-table td {
    padding: 8px;
    text-align: right;
    border: 1px solid #ddd;
}

.totals-table tr.total td {
    font-size: 1.2rem;
    font-weight: bold;
    background-color: #f8f9fa;
}

/* Nota */
.note {
    margin-top: 20px;
    font-size: 0.9rem;
    text-align: center;
    color: #777;
}

/* Botón */
.btn {
    margin-bottom: 20px;
    padding: 10px 15px;
    background-color: #007bff;
    color: white;
    border: none;
    cursor: pointer;
}

.btn:hover {
    background-color: #0056b3;
}

.totals-container {
    clear: both;
}

.amount-column {
    width: 213px;
    /* Ajusta el ancho según necesites */
    text-align: right;
}

.refund-row {
    background-color: #ffcccc;
    border-radius: 4px;
}

.refund-row * {
    text-decoration: line-through;
}
</style>
